/**
 * Stimulsoft Reports.JS Compatibility Middleware
 * 
 * Provides backward compatibility transformations for report templates
 * prior to loading into Stimulsoft Reports.JS 2026.3.4 Viewer or Designer.
 */

export interface CompatibilityRule {
  id: string
  name: string
  description?: string
  enabled?: boolean
  detect?: (content: string) => boolean
  apply: (content: string) => string
}

interface Token {
  type: 'string' | 'paren' | 'bracket' | 'operator' | 'text'
  raw: string
  value?: string
  depth: number
}

/**
 * Tokenizes an expression string taking quotes, escaped JSON quotes,
 * parentheses, and bracket depth into account.
 */
function tokenizeExpression(input: string): Token[] {
  const tokens: Token[] = []
  let i = 0
  let parenDepth = 0
  let bracketDepth = 0

  while (i < input.length) {
    const ch = input[i]

    // Single-quoted string literal '...'
    if (ch === "'") {
      let str = "'"
      i++
      while (i < input.length && input[i] !== "'") {
        if (input[i] === '\\' && i + 1 < input.length) {
          str += input[i++]
        }
        str += input[i++]
      }
      if (i < input.length) {
        str += input[i++]
      }
      tokens.push({ type: 'string', raw: str, depth: parenDepth })
      continue
    }

    // Double-quoted string literal "..." or escaped JSON quote \"...\"
    if (ch === '"' || (ch === '\\' && input[i + 1] === '"')) {
      const isEscaped = ch === '\\'
      const quoteToken = isEscaped ? '\\"' : '"'
      let str = quoteToken
      i += quoteToken.length

      while (i < input.length) {
        if (isEscaped && input[i] === '\\' && input[i + 1] === '"') {
          str += '\\"'
          i += 2
          break
        } else if (!isEscaped && input[i] === '"') {
          str += '"'
          i++
          break
        } else if (input[i] === '\\' && i + 1 < input.length) {
          str += input[i++]
          str += input[i++]
        } else {
          str += input[i++]
        }
      }
      tokens.push({ type: 'string', raw: str, depth: parenDepth })
      continue
    }

    // Parentheses
    if (ch === '(') {
      parenDepth++
      tokens.push({ type: 'paren', value: '(', raw: ch, depth: parenDepth })
      i++
      continue
    }
    if (ch === ')') {
      tokens.push({ type: 'paren', value: ')', raw: ch, depth: parenDepth })
      parenDepth = Math.max(0, parenDepth - 1)
      i++
      continue
    }

    // Square brackets
    if (ch === '[') {
      bracketDepth++
      tokens.push({ type: 'bracket', value: '[', raw: ch, depth: bracketDepth })
      i++
      continue
    }
    if (ch === ']') {
      tokens.push({ type: 'bracket', value: ']', raw: ch, depth: bracketDepth })
      bracketDepth = Math.max(0, bracketDepth - 1)
      i++
      continue
    }

    // Top-level ternary operator '?' (not inside parentheses, brackets, or null-coalescing '??' / optional chaining '?.')
    if (ch === '?' && parenDepth === 0 && bracketDepth === 0) {
      if (input[i + 1] === '?' || input[i + 1] === '.') {
        const op = input.slice(i, i + 2)
        tokens.push({ type: 'operator', value: op, raw: op, depth: 0 })
        i += 2
        continue
      }
      tokens.push({ type: 'operator', value: '?', raw: ch, depth: 0 })
      i++
      continue
    }

    // Top-level ':' (not inside parentheses or brackets)
    if (ch === ':' && parenDepth === 0 && bracketDepth === 0) {
      tokens.push({ type: 'operator', value: ':', raw: ch, depth: 0 })
      i++
      continue
    }

    tokens.push({ type: 'text', raw: ch, depth: parenDepth })
    i++
  }

  return tokens
}

/**
 * Checks if a condition expression already contains explicit null or undefined checks.
 */
function hasExplicitNullCheck(condition: string): boolean {
  return (
    /\b(null|undefined)\b/.test(condition) ||
    /(!=|!==|==|===)\s*null\b/.test(condition) ||
    /\bnull\s*(!=|!==|==|===)/.test(condition)
  )
}

/**
 * Checks if a condition expression contains relational or equality comparison operators.
 */
function hasComparisonOperator(condition: string): boolean {
  return /[><=!]=?/.test(condition)
}

/**
 * Trims leading and trailing whitespace, including escaped newlines/tabs in JSON strings.
 */
function cleanWhitespace(s: string): string {
  return s.replace(/^(?:\s|\\[rnt])+|(?:\s|\\[rnt])+$/g, '')
}

/**
 * Transforms an inner expression string if it matches the legacy ternary pattern `A ? A : B`.
 */
function transformInnerExpression(expr: string): string {
  const tokens = tokenizeExpression(expr)
  if (!tokens || tokens.length === 0) return expr

  // Find the top-level '?'
  const questionIndex = tokens.findIndex(t => t.type === 'operator' && t.value === '?')
  if (questionIndex === -1) return expr

  // Find the top-level ':' after '?'
  const colonIndex = tokens.findIndex((t, idx) => idx > questionIndex && t.type === 'operator' && t.value === ':')
  if (colonIndex === -1) return expr

  const conditionTokens = tokens.slice(0, questionIndex)
  const trueTokens = tokens.slice(questionIndex + 1, colonIndex)
  const falseTokens = tokens.slice(colonIndex + 1)

  const conditionStr = cleanWhitespace(conditionTokens.map(t => t.raw).join(''))
  const trueStr = cleanWhitespace(trueTokens.map(t => t.raw).join(''))
  const falseStr = cleanWhitespace(falseTokens.map(t => t.raw).join(''))

  // 1. Condition must be non-empty and identical to the true branch
  if (!conditionStr || conditionStr !== trueStr) {
    return expr
  }

  // 2. Avoid modifying expressions that already have explicit null checks
  if (hasExplicitNullCheck(conditionStr)) {
    return expr
  }

  // 3. Avoid modifying expressions with relational comparisons (e.g. A > 0, A == B)
  if (hasComparisonOperator(conditionStr)) {
    return expr
  }

  // Extract leading and trailing whitespace from the inner expression
  const leadingWs = expr.match(/^(?:\s|\\[rnt])*/)?.[0] ?? ''
  const trailingWs = expr.match(/(?:\s|\\[rnt])*$/)?.[0] ?? ''

  // Recursively transform any chained ternaries in the false branch
  const transformedFalseStr = transformInnerExpression(falseStr)

  return `${leadingWs}${conditionStr} != null ? ${trueStr} : ${transformedFalseStr}${trailingWs}`
}

/**
 * Finds expressions enclosed in `{ ... }` and applies transformation to the inner content.
 */
export function transformExpressions(content: string): string {
  if (!content || typeof content !== 'string') return content

  return content.replace(/\{([^{}]+)\}/g, (match, inner) => {
    const transformed = transformInnerExpression(inner)
    return `{${transformed}}`
  })
}

/**
 * Detects whether the content contains any legacy `{ A ? A : B }` expressions that require transformation.
 */
export function detectLegacyTernaryNullCheck(content: string): boolean {
  if (!content || typeof content !== 'string') return false
  const transformed = transformExpressions(content)
  return transformed !== content
}

/**
 * Built-in Rule: Fix legacy ternary null checks `{ A ? A : B }` -> `{ A != null ? A : B }`
 */
export const nullCheckTernaryRule: CompatibilityRule = {
  id: 'null-check-ternary',
  name: 'Legacy Ternary Null Check Migration',
  description: 'Converts legacy expressions `{ A ? A : B }` to `{ A != null ? A : B }` for Stimulsoft Reports.JS 2026.3.4 compatibility.',
  enabled: true,
  detect: detectLegacyTernaryNullCheck,
  apply: transformExpressions
}

/**
 * StimulsoftCompatibility Manager
 */
export class StimulsoftCompatibility {
  private rules: CompatibilityRule[] = []

  constructor(initialRules: CompatibilityRule[] = []) {
    this.rules = [...initialRules]
  }

  /**
   * Registers a compatibility rule. Overwrites if rule with same id exists.
   */
  registerRule(rule: CompatibilityRule): void {
    const idx = this.rules.findIndex(r => r.id === rule.id)
    if (idx >= 0) {
      this.rules[idx] = rule
    } else {
      this.rules.push(rule)
    }
  }

  /**
   * Unregisters a compatibility rule by id.
   */
  unregisterRule(id: string): void {
    this.rules = this.rules.filter(r => r.id !== id)
  }

  /**
   * Retrieves a rule by id.
   */
  getRule(id: string): CompatibilityRule | undefined {
    return this.rules.find(r => r.id === id)
  }

  /**
   * Returns a copy of all registered rules.
   */
  getRules(): CompatibilityRule[] {
    return [...this.rules]
  }

  /**
   * Enables or disables a rule by id.
   */
  enableRule(id: string, enabled = true): void {
    const rule = this.getRule(id)
    if (rule) {
      rule.enabled = enabled
    }
  }

  /**
   * Detects if any enabled rule identifies compatibility issues in the report content.
   */
  detect(content: string): boolean {
    if (!content || typeof content !== 'string') return false
    return this.rules.some(rule => (rule.enabled ?? true) && (rule.detect ? rule.detect(content) : false))
  }

  /**
   * Applies all enabled compatibility rules in sequence to the report.
   * Supports both report JSON strings and parsed report objects.
   */
  apply(report: string): string
  apply<T extends Record<string, any>>(report: T): T
  apply<T extends string | Record<string, any>>(report: T): T {
    if (!report) return report

    if (typeof report === 'string') {
      let result = report
      for (const rule of this.rules) {
        if (rule.enabled ?? true) {
          result = rule.apply(result)
        }
      }
      return result as unknown as T
    }

    if (typeof report === 'object') {
      try {
        const jsonStr = JSON.stringify(report)
        let transformedStr = jsonStr
        for (const rule of this.rules) {
          if (rule.enabled ?? true) {
            transformedStr = rule.apply(transformedStr)
          }
        }
        return JSON.parse(transformedStr) as T
      } catch (err) {
        return report
      }
    }

    return report
  }
}

/**
 * Singleton instance preconfigured with Stimulsoft Reports.JS 2026.3.4 compatibility rules.
 */
export const stimulsoftCompatibility = new StimulsoftCompatibility([nullCheckTernaryRule])

/**
 * Convenience helper to apply compatibility rules to a report string or object.
 */
export function applyStimulsoftCompatibility(report: string): string
export function applyStimulsoftCompatibility<T extends Record<string, any>>(report: T): T
export function applyStimulsoftCompatibility<T extends string | Record<string, any>>(report: T): T {
  return stimulsoftCompatibility.apply(report)
}

export default stimulsoftCompatibility
